import type { Moment } from "moment";
import { taskService } from "@miplatform/api";
import {
  CalendarEventType,
  CalendarType,
  IApiResponse,
  IAttempt,
  ICalendarConfig,
  ICalendarSet,
} from "@miplatform/types";
import { endOf, getGUIDArray, getOrFilter, startOf } from "@miplatform/utils";

const getDateRangeFilter = (range: { ge: Date; le: Date }) =>
  getOrFilter(
    {
      scheduledStartDate: range,
      startDate: null,
    },
    {
      startDate: range,
    }
  );

export const loadCalendarEvents = async ({
  calendarSetConfig,
  calendarConfig,
  startDate,
  endDate,
  userIds,
}: LoadCalendarEventsPayload): IApiResponse<IAttempt[]> => {
  const isWorkOrderCalendar =
    calendarSetConfig.eventType === CalendarEventType.WorkOrder;
  const getAttempts = isWorkOrderCalendar
    ? taskService.attempts.getAllWorkOrders
    : taskService.attempts.getAll;

  const templates = calendarConfig.workTemplateBases ?? [];

  const dateFilter = getDateRangeFilter({
    ge: startOf("day", startDate).toDate(),
    le: endOf("day", endDate ?? startDate).toDate(),
  });

  const assignableUserIds =
    userIds ?? calendarSetConfig.assignableUsers?.map((user) => user.id) ?? [];
  const userFilter =
    assignableUserIds.length > 0
      ? {
          assignedUserId: {
            in: getGUIDArray(assignableUserIds),
          },
        }
      : {};

  return getAttempts({
    filters: {
      ...calendarConfig.filter,
      ...dateFilter,
      ...userFilter,
      ...(calendarConfig.calendarType === CalendarType.HoldsList
        ? {
            isOrderFollowupDue: false,
          }
        : {
            ...dateFilter,
          }),
      ...(templates.length > 0 && {
        assignment: {
          [calendarSetConfig.eventType]: {
            [`${calendarSetConfig.eventType}TemplateId`]: {
              in: getGUIDArray(templates.map((template) => template.id)),
            },
            ...(calendarConfig.calendarType === CalendarType.HoldsList && {
              holdCalendarId: {
                type: "guid",
                value: calendarConfig.id,
              },
            }),
          },
        },
      }),
    },
    params: {
      includeWorkOrders: !isWorkOrderCalendar,
    },
  });
};

type LoadCalendarEventsPayload = {
  calendarSetConfig: ICalendarSet;
  calendarConfig: ICalendarConfig;
  startDate: Moment;
  endDate?: Moment;
  userIds?: string[] | null;
};
